// User authentication utilities (localStorage-based MVP)
// NOTE: For production, migrate to a real backend with proper authentication

import { User, SignupData, AuthState } from '../types/user';

const AUTH_STORAGE_KEY = 'expense_tracker_auth';
const USERS_STORAGE_KEY = 'expense_tracker_users';
const TRIAL_DURATION_DAYS = 14;

// Test account for development
const TEST_USER: User = {
  id: 'user_test_dev',
  email: 'test@free1099calc.com',
  firstName: 'Test',
  lastName: 'User',
  createdAt: '2025-01-01T00:00:00.000Z',
  subscriptionStatus: 'active',
  trialStartDate: '2025-01-01T00:00:00.000Z',
  trialEndDate: '2025-01-15T00:00:00.000Z',
  paymentMethodId: 'pm_test_12345',
  customerId: 'cus_test_12345',
  lastPaymentDate: '2025-01-15T00:00:00.000Z',
};

// Get all users (for MVP multi-user support)
const getAllUsers = (): User[] => {
  try {
    const usersData = localStorage.getItem(USERS_STORAGE_KEY);
    if (!usersData) {
      // Initialize with test user if no users exist
      return [TEST_USER];
    }
    const users = JSON.parse(usersData) as User[];
    
    // Ensure test user always exists
    const hasTestUser = users.some(u => u.email === TEST_USER.email);
    if (!hasTestUser) {
      users.push(TEST_USER);
    }
    
    return users;
  } catch (error) {
    // Return test user if any error
    return [TEST_USER];
  }
};

// Save all users
const saveAllUsers = (users: User[]): void => {
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users));
};

// Get current authenticated user
export const getCurrentUser = (): User | null => {
  try {
    const authData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!authData) return null;
    return JSON.parse(authData) as User;
  } catch (error) {
    return null;
  }
};

// Create new user account with trial period
export const createUserAccount = (signupData: SignupData): User => {
  const now = new Date();
  const trialEnd = new Date();
  trialEnd.setDate(trialEnd.getDate() + TRIAL_DURATION_DAYS);

  const newUser: User = {
    id: `user_${Date.now()}`, // In production, this would come from backend
    email: signupData.email.toLowerCase(), // Normalize email
    firstName: signupData.firstName,
    lastName: signupData.lastName,
    createdAt: now.toISOString(),
    subscriptionStatus: 'trial',
    trialStartDate: now.toISOString(),
    trialEndDate: trialEnd.toISOString(),
    paymentMethodId: signupData.paymentMethodId,
    customerId: null, // Would be set by Stripe on backend
    lastPaymentDate: null,
  };

  // Add to users array
  const users = getAllUsers();
  users.push(newUser);
  saveAllUsers(users);
  
  // Set as current user
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(newUser));
  
  // Notify the app about auth change (for header update)
  window.dispatchEvent(new Event('auth-change'));
  
  return newUser;
};

// Login existing user
export const loginUser = (email: string, password: string): User | null => {
  // In production, this would validate credentials against backend
  // For MVP, we'll check if user exists in users array
  const users = getAllUsers();
  const normalizedEmail = email.toLowerCase();
  
  const user = users.find(u => u.email.toLowerCase() === normalizedEmail);
  
  if (user) {
    // Set as current user
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
    
    // Notify the app about auth change (for header update)
    window.dispatchEvent(new Event('auth-change'));
    
    return user;
  }
  
  return null;
};

// Logout user
export const logoutUser = (): void => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  
  // Notify the app about auth change (for header update)
  window.dispatchEvent(new Event('auth-change'));
};

// Update user subscription to active (after payment)
export const activateSubscription = (userId: string): void => {
  const user = getCurrentUser();
  if (user && user.id === userId) {
    user.subscriptionStatus = 'active';
    user.lastPaymentDate = new Date().toISOString();
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
  }
};

// Check if trial has expired and needs payment
export const checkTrialExpiration = (): boolean => {
  const user = getCurrentUser();
  if (!user || user.subscriptionStatus !== 'trial') return false;
  
  if (user.trialEndDate) {
    const now = new Date();
    const trialEnd = new Date(user.trialEndDate);
    
    if (now >= trialEnd) {
      // Trial has expired
      user.subscriptionStatus = 'expired';
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
      return true;
    }
  }
  
  return false;
};

// Debug utility - get all registered users
export const getAllRegisteredUsers = (): User[] => {
  return getAllUsers();
};

// Mock Stripe payment setup (would be real Stripe API calls in production)
export const setupPaymentMethod = async (cardDetails: any): Promise<string> => {
  // In production, this would call Stripe.js to create a payment method
  // For MVP, we'll just return a mock payment method ID
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(`pm_mock_${Date.now()}`);
    }, 500);
  });
};

// Mock Stripe subscription creation with trial
export const createSubscriptionWithTrial = async (
  customerId: string,
  paymentMethodId: string
): Promise<{ subscriptionId: string; clientSecret: string }> => {
  // In production, this would create a Stripe subscription with:
  // - 14-day trial period
  // - $5.99/month subscription after trial ends
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        subscriptionId: `sub_mock_${Date.now()}`,
        clientSecret: `seti_mock_${Date.now()}`,
      });
    }, 500);
  });
};

// Check if user needs to provide payment after trial
export const needsPayment = (): boolean => {
  const user = getCurrentUser();
  if (!user) return false;
  
  return user.subscriptionStatus === 'expired' && !user.lastPaymentDate;
};
